import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HoldingsTable = ({ holdings, onBuyMore, onSell, onViewDetails }) => {
  const [sortBy, setSortBy] = useState('symbol');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const toggleRowExpansion = (symbol) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(symbol)) {
      newExpanded.delete(symbol);
    } else {
      newExpanded.add(symbol);
    }
    setExpandedRows(newExpanded);
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 text-left hover:text-foreground transition-colors"
    >
      <span>{children}</span>
      {sortBy === column && (
        <Icon name={sortOrder === 'asc' ? "ChevronUp" : "ChevronDown"} size={16} />
      )}
    </button>
  );

  return (
    <div className="card-elevated">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th><SortButton column="symbol">Symbol</SortButton></th>
              <th><SortButton column="companyName">Company</SortButton></th>
              <th><SortButton column="shares">Shares</SortButton></th>
              <th><SortButton column="avgCost">Avg Cost</SortButton></th>
              <th><SortButton column="currentPrice">Current Price</SortButton></th>
              <th><SortButton column="marketValue">Market Value</SortButton></th>
              <th><SortButton column="gainLoss">Gain/Loss</SortButton></th>
              <th><SortButton column="gainLossPercent">%</SortButton></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedHoldings.map((holding) => {
              const gainLoss = holding.marketValue - (holding.shares * holding.avgCost);
              const gainLossPercent = ((holding.currentPrice - holding.avgCost) / holding.avgCost) * 100;
              
              return (
                <tr key={holding.symbol}>
                  <td>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-primary">{holding.symbol.charAt(0)}</span>
                      </div>
                      <span className="font-medium">{holding.symbol}</span>
                    </div>
                  </td>
                  <td className="text-sm text-muted-foreground">{holding.companyName}</td>
                  <td className="font-mono">{holding.shares.toLocaleString()}</td>
                                  <td className="font-mono">₹{holding.avgCost.toFixed(2)}</td>
                <td className="font-mono">₹{holding.currentPrice.toFixed(2)}</td>
                <td className="font-mono">₹{holding.marketValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className={`font-mono ${gainLoss >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {gainLoss >= 0 ? '+' : ''}₹{gainLoss.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
                  <td className={`font-mono ${gainLossPercent >= 0 ? 'text-success' : 'text-destructive'}`}>
                    {gainLossPercent >= 0 ? '+' : ''}{gainLossPercent.toFixed(2)}%
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="xs" onClick={() => onBuyMore(holding.symbol)}>
                        <Icon name="Plus" size={14} />
                      </Button>
                      <Button variant="ghost" size="xs" onClick={() => onSell(holding.symbol)}>
                        <Icon name="Minus" size={14} />
                      </Button>
                      <Button variant="ghost" size="xs" onClick={() => onViewDetails(holding.symbol)}>
                        <Icon name="Eye" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Holdings</h3>
        </div>
        <div className="divide-y divide-border">
          {sortedHoldings.map((holding) => {
            const gainLoss = holding.marketValue - (holding.shares * holding.avgCost);
            const gainLossPercent = ((holding.currentPrice - holding.avgCost) / holding.avgCost) * 100;
            const isExpanded = expandedRows.has(holding.symbol);
            
            return (
              <div key={holding.symbol} className="p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleRowExpansion(holding.symbol)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">{holding.symbol.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{holding.symbol}</p>
                      <p className="text-sm text-muted-foreground">{holding.companyName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                                    <p className="font-mono text-foreground">₹{holding.marketValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                <p className={`text-sm font-mono ${gainLoss >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {gainLoss >= 0 ? '+' : ''}₹{gainLoss.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                  </div>
                  <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} className="text-muted-foreground" />
                </div>
                
                {isExpanded && (
                  <div className="mt-4 space-y-3 animate-slide-in">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Shares:</span>
                        <span className="ml-2 font-mono">{holding.shares.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Avg Cost:</span>
                        <span className="ml-2 font-mono">₹{holding.avgCost.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Current Price:</span>
                        <span className="ml-2 font-mono">₹{holding.currentPrice.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">% Change:</span>
                        <span className={`ml-2 font-mono ${gainLossPercent >= 0 ? 'text-success' : 'text-destructive'}`}>
                          {gainLossPercent >= 0 ? '+' : ''}{gainLossPercent.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => onBuyMore(holding.symbol)} fullWidth>
                        <Icon name="Plus" size={16} />
                        <span className="ml-2">Buy More</span>
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onSell(holding.symbol)} fullWidth>
                        <Icon name="Minus" size={16} />
                        <span className="ml-2">Sell</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onViewDetails(holding.symbol)} fullWidth>
                        <Icon name="Eye" size={16} />
                        <span className="ml-2">Details</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HoldingsTable;